import { z } from 'zod';
import { normalizarTexto } from './fonetica.js';
import { telefonoArSchema } from './telefono.js';
/**
 * El Conjunto Mínimo de Datos de identificación (Res. 2524/2019, ADR-020 E2.1).
 *
 * Los ocho elementos con los que todo establecimiento de salud identifica a una
 * persona: primer nombre, otros nombres, primer apellido, otros apellidos, tipo
 * de documento, número de documento, sexo según documento y fecha de
 * nacimiento. Como el teléfono, **una sola regla para toda entrada del
 * sistema**: la pantalla de alta, el alta sintética, el seed y el arnés pasan por
 * estos schemas, y lo que sale del `parse` ya está normalizado.
 *
 * Nada de esto se guarda en claro en nuestra base: se cifra antes de escribirse
 * (`packages/identidad`). El sexo es la excepción, por definición de Fundación
 * (E2.2), y es el del documento, no la identidad de género, que el sistema no
 * pregunta.
 */
// ── Tipo de documento ──────────────────────────────────────────────────────
/** La lista de la guía, en el orden en que la enumera. */
export const tipoDocumentoSchema = z.enum([
    'DNI',
    'LIBRETA_CIVICA',
    'LIBRETA_ENROLAMIENTO',
    'CEDULA_PFA',
    'PASAPORTE',
    'CEDULA_EXTRANJERA',
    'DNI_EXTRANJERO',
    'PARTIDA_NACIMIENTO',
]);
/** Cómo se nombra cada tipo en pantalla: con los nombres de la guía. */
export const NOMBRE_DEL_TIPO_DE_DOCUMENTO = {
    DNI: 'DNI',
    LIBRETA_CIVICA: 'Libreta Cívica',
    LIBRETA_ENROLAMIENTO: 'Libreta de Enrolamiento',
    CEDULA_PFA: 'Cédula de Identidad PFA',
    PASAPORTE: 'Pasaporte',
    CEDULA_EXTRANJERA: 'Cédula extranjera',
    DNI_EXTRANJERO: 'DNI extranjero',
    PARTIDA_NACIMIENTO: 'Partida de nacimiento',
};
/** Los tres que la guía acompaña con el país que los emitió. */
export const TIPOS_CON_PAIS = Object.freeze([
    'PASAPORTE',
    'CEDULA_EXTRANJERA',
    'DNI_EXTRANJERO',
]);
export function exigePais(tipo) {
    return TIPOS_CON_PAIS.includes(tipo);
}
// ── País de emisión ────────────────────────────────────────────────────────
/**
 * Códigos de región que el registro de Unicode conoce y que **no** son países:
 * agrupaciones (Unión Europea, Naciones Unidas), el desconocido y los
 * pseudo-países de prueba. ISO 3166-1 no los incluye.
 */
const REGIONES_QUE_NO_SON_PAISES = new Set(['EU', 'EZ', 'UN', 'QO', 'XA', 'XB', 'ZZ']);
const nombresDeRegion = new Intl.DisplayNames(['es'], { type: 'region', fallback: 'none' });
/**
 * El nombre en castellano de un código ISO 3166-1 alfa-2, o `null` si no es un
 * país.
 *
 * Se apoya en los datos de Unicode que ya trae el motor de JavaScript (Node y
 * cualquier navegador): mantener a mano una lista de 249 países es una lista
 * que se desactualiza, y ésta la actualiza la plataforma.
 */
export function nombreDelPais(codigo) {
    if (!/^[A-Z]{2}$/.test(codigo) || REGIONES_QUE_NO_SON_PAISES.has(codigo))
        return null;
    return nombresDeRegion.of(codigo) ?? null;
}
export const MENSAJE_PAIS_INVALIDO = 'Elegí el país que emitió el documento.';
export const paisSchema = z
    .string()
    .transform((valor) => valor.trim().toUpperCase())
    .refine((valor) => nombreDelPais(valor) !== null, MENSAJE_PAIS_INVALIDO);
// ── Número de documento ────────────────────────────────────────────────────
export const MENSAJE_DNI_INVALIDO = 'Revisá el número de DNI: son entre 6 y 8 números, sin puntos, como 12345678.';
export const MENSAJE_DOCUMENTO_INVALIDO = 'Revisá el número de documento: sólo letras y números, entre 3 y 20.';
/**
 * El número tal como se va a guardar: sin puntos, espacios ni guiones, en
 * mayúsculas. `12.345.678` y `12345678` son el mismo documento, y la huella del
 * buscador tiene que dar la misma para los dos.
 */
export function normalizarNumeroDeDocumento(numero) {
    return numero.replace(/[\s.-]/g, '').toUpperCase();
}
/**
 * Las reglas por tipo.
 *
 * El DNI es numérico de 6 a 8 dígitos. Las libretas viejas conservaron su número
 * al pasar al DNI, así que siguen la misma regla. El resto admite letras
 * (pasaportes, cédulas de otros países): alfanumérico acotado.
 */
const REGLA_DEL_NUMERO = {
    DNI: { patron: /^\d{6,8}$/, mensaje: MENSAJE_DNI_INVALIDO },
    LIBRETA_CIVICA: { patron: /^\d{6,8}$/, mensaje: MENSAJE_DNI_INVALIDO },
    LIBRETA_ENROLAMIENTO: { patron: /^\d{6,8}$/, mensaje: MENSAJE_DNI_INVALIDO },
    CEDULA_PFA: { patron: /^[A-Z0-9]{3,20}$/, mensaje: MENSAJE_DOCUMENTO_INVALIDO },
    PASAPORTE: { patron: /^[A-Z0-9]{3,20}$/, mensaje: MENSAJE_DOCUMENTO_INVALIDO },
    CEDULA_EXTRANJERA: { patron: /^[A-Z0-9]{3,20}$/, mensaje: MENSAJE_DOCUMENTO_INVALIDO },
    DNI_EXTRANJERO: { patron: /^[A-Z0-9]{3,20}$/, mensaje: MENSAJE_DOCUMENTO_INVALIDO },
    PARTIDA_NACIMIENTO: { patron: /^[A-Z0-9]{3,20}$/, mensaje: MENSAJE_DOCUMENTO_INVALIDO },
};
/** El problema del número para ese tipo, o `null` si está bien. */
export function problemaDelNumero(tipo, numero) {
    const regla = REGLA_DEL_NUMERO[tipo];
    return regla.patron.test(normalizarNumeroDeDocumento(numero)) ? null : regla.mensaje;
}
// ── Sexo según documento ───────────────────────────────────────────────────
/**
 * El campo "Sexo" tal como está impreso en el documento. La X existe desde 2021.
 * Sin este dato no hay alta: es parte de la llave (E1.1).
 */
export const sexoSegunDocumentoSchema = z.enum(['F', 'M', 'X'], {
    error: 'Elegí el sexo que figura en el documento.',
});
// ── Fecha de nacimiento ────────────────────────────────────────────────────
/**
 * La edad para entrar al programa.
 *
 * El mínimo es **definición de Fundación del 28/10/2026** (respuesta 2.9): 60
 * años cumplidos o más, contados al egreso, que es el día en que la persona
 * entra al programa. El máximo no es una regla clínica: ataja un error de tipeo.
 */
export const EDAD_MINIMA_AL_INGRESO = 60;
export const EDAD_MAXIMA_AL_INGRESO = 120;
// PROPUESTO · pendiente de validación de Fundación (respuesta 2.9 del 28/10)
export const MENSAJE_EDAD_MINIMA = 'El programa es para personas de 60 años o más.';
// PROPUESTO · pendiente de validación de Fundación (respuesta 2.9 del 28/10)
export const MENSAJE_FECHA_DE_NACIMIENTO_INVALIDA = 'Revisá la fecha de nacimiento.';
/**
 * Años cumplidos a una fecha, contados en calendario y no en milisegundos: el
 * día del cumpleaños la edad sube, ni un día antes. Las dos fechas son días
 * (`YYYY-MM-DD`), sin hora, así que no hay zona horaria que la corra.
 */
export function edadEnAnios(fechaNacimiento, alDia) {
    const [anioN, mesN, diaN] = fechaNacimiento.split('-').map(Number);
    const [anio, mes, dia] = alDia.split('-').map(Number);
    const yaCumplio = mes > mesN || (mes === mesN && dia >= diaN);
    return anio - anioN - (yaCumplio ? 0 : 1);
}
/**
 * Qué le pasa a una fecha de nacimiento a ese día, o `null` si está bien.
 *
 * Son dos problemas distintos y se dicen distinto: nacer después de ese día o
 * pasar los 120 años es un error de carga; tener menos de 60 es una persona que
 * el programa no recibe.
 */
export function problemaDeLaEdad(fechaNacimiento, alDia) {
    if (fechaNacimiento > alDia)
        return MENSAJE_FECHA_DE_NACIMIENTO_INVALIDA;
    const edad = edadEnAnios(fechaNacimiento, alDia);
    if (edad > EDAD_MAXIMA_AL_INGRESO)
        return MENSAJE_FECHA_DE_NACIMIENTO_INVALIDA;
    return edad < EDAD_MINIMA_AL_INGRESO ? MENSAJE_EDAD_MINIMA : null;
}
/**
 * La fecha de nacimiento, validada contra un día.
 *
 * El día se inyecta: un schema que lee el reloj adentro cambia su veredicto
 * según la hora a la que corre el test (la familia de P-100).
 */
export function fechaDeNacimientoSchema(alDia) {
    return z.iso
        .date({ error: MENSAJE_FECHA_DE_NACIMIENTO_INVALIDA, abort: true })
        .superRefine((fecha, ctx) => {
        const problema = problemaDeLaEdad(fecha, alDia);
        if (problema)
            ctx.addIssue({ code: 'custom', message: problema });
    });
}
// ── Nombres y apellidos ────────────────────────────────────────────────────
export const MENSAJE_NOMBRE_INVALIDO = 'Revisá el nombre: sólo letras, espacios, apóstrofos y guiones, hasta 60.';
/**
 * Un nombre o apellido, tal como la persona lo escribe en su documento.
 *
 * Se guarda con sus tildes y su Ñ (se cifra así y así se muestra en la ficha);
 * la normalización sin tildes es sólo para las huellas. Se admiten letras de
 * cualquier alfabeto latino, espacios, apóstrofo y guion: D'Angelo, Pérez-Díaz.
 */
const nombreSchema = z
    .string()
    .transform((valor) => valor.replace(/\s+/g, ' ').trim())
    .refine((valor) => valor.length >= 1 && valor.length <= 60 && /^[\p{L}][\p{L}\p{M} '’-]*$/u.test(valor), MENSAJE_NOMBRE_INVALIDO);
const nombreOpcionalSchema = z
    .string()
    .transform((valor) => valor.replace(/\s+/g, ' ').trim())
    .refine((valor) => valor.length === 0 || (valor.length <= 60 && /^[\p{L}][\p{L}\p{M} '’-]*$/u.test(valor)), MENSAJE_NOMBRE_INVALIDO)
    .optional();
/**
 * Mayúsculas, sin tildes, espacios colapsados: la forma de las huellas.
 * Re-exportada con el nombre del dominio para que quien calcula una huella no
 * tenga que saber que vive en la fonética.
 */
export const normalizarNombre = normalizarTexto;
// ── El conjunto ────────────────────────────────────────────────────────────
/**
 * Los ocho elementos más el celular, validados juntos.
 *
 * El país se exige sólo para los tres tipos extranjeros y se rechaza en los
 * demás: un DNI argentino con país es un error de carga, no un dato de más.
 */
export function conjuntoMinimoSchema(alDia) {
    return z
        .object({
        primerNombre: nombreSchema,
        otrosNombres: nombreOpcionalSchema,
        primerApellido: nombreSchema,
        otrosApellidos: nombreOpcionalSchema,
        tipoDocumento: tipoDocumentoSchema,
        paisEmision: paisSchema.optional(),
        numeroDocumento: z.string().transform(normalizarNumeroDeDocumento),
        sexoSegunDocumento: sexoSegunDocumentoSchema,
        fechaNacimiento: fechaDeNacimientoSchema(alDia),
        celular: telefonoArSchema,
    })
        .superRefine((datos, ctx) => {
        const problema = problemaDelNumero(datos.tipoDocumento, datos.numeroDocumento);
        if (problema)
            ctx.addIssue({ code: 'custom', path: ['numeroDocumento'], message: problema });
        if (exigePais(datos.tipoDocumento) && !datos.paisEmision) {
            ctx.addIssue({ code: 'custom', path: ['paisEmision'], message: MENSAJE_PAIS_INVALIDO });
        }
        if (!exigePais(datos.tipoDocumento) && datos.paisEmision) {
            ctx.addIssue({
                code: 'custom',
                path: ['paisEmision'],
                message: 'Este tipo de documento no lleva país: es argentino.',
            });
        }
    })
        .transform(({ otrosNombres, otrosApellidos, paisEmision, ...resto }) => ({
        ...resto,
        // Vacío y ausente son lo mismo: la persona tiene un solo nombre.
        ...(otrosNombres ? { otrosNombres } : {}),
        ...(otrosApellidos ? { otrosApellidos } : {}),
        ...(paisEmision ? { paisEmision } : {}),
    }));
}
/** Nombre completo para mostrar: todos los nombres y después todos los apellidos. */
export function nombreCompleto(datos) {
    return {
        nombres: [datos.primerNombre, datos.otrosNombres].filter(Boolean).join(' '),
        apellidos: [datos.primerApellido, datos.otrosApellidos].filter(Boolean).join(' '),
    };
}
//# sourceMappingURL=identidad.js.map